import { NgModule, ErrorHandler } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { ServiceWorkerModule } from '@angular/service-worker';

import { AccountConfigModule } from '@abp/ng.account/config';
import { CoreModule } from '@abp/ng.core';
import { IdentityConfigModule } from '@abp/ng.identity/config';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import { TenantManagementConfigModule } from '@abp/ng.tenant-management/config';
import { ThemeBasicModule } from '@abp/ng.theme.basic';
import { ThemeSharedModule } from '@abp/ng.theme.shared';

import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { en_US } from 'ng-zorro-antd/i18n';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzSelectModule } from 'ng-zorro-antd/select';

import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { NgxsModule } from '@ngxs/store';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { IconsProviderModule } from './icons-provider.module';

import { VALIDATION_BLUEPRINTS } from '@ngx-validate/core';
import { VALIDATION_ERROR_TEMPLATE } from '@ngx-validate/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';

import { CoreDirectivesModule } from './core/directive/core-directives.module';
import { SiteLayoutComponent } from './core/layout/site-layout/site-layout.component';
import { SiteHeaderComponent } from './core/layout/site-header/site-header.component';
import { SiteSiderComponent } from './core/layout/site-sider/site-sider.component';
import { BreadcrumbComponent } from './core/layout/site-breadcrumb/site-breadcrumb.component';

import { PageLayoutComponent } from './core/layout/page-layout/page-layout.component';
import { NotfoundComponent } from './core/error/notfound/notfound.component';
import { SiteLoaderComponent } from './core/layout/site-loader/site-loader.component';
import { ValidationComponent } from './core/error/validation/validation.component';
import { GoogleSheetsDbService } from 'ng-google-sheets-db';
import { PortalLayoutComponent } from './core/layout/portal-layout/portal-layout.component';
import { PortalHeaderComponent } from './core/layout/portal-header/portal-header.component';
import { AppState } from './app.state';
import { VendorLayoutComponent } from './core/layout/vendor-layout/vendor-layout.component';
import { ChangePasswordComponent } from './account/change-password/change-password.component';
import { ManageProfileComponent } from './account/manage-profile/manage-profile.component';
import { SupportComponent } from './core/support/support/support.component';
import { AppErrorHandler } from './core/error/error.handler';
import { NotificationsComponent } from './core/notifications/notifications.component';
import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { ManageProfileVendorComponent } from './account/manage-profile-vendor/manage-profile-vendor.component';
import { TenantInterceptor } from './core/interceptor/tenant-interceptor';
import { AppearanceComponent } from './account/manage-profile/appearance/appearance.component';
import { CallbackComponent } from './auth/callback.component';
import { SignoutCallbackComponent } from './auth/signout-callback.component';
import { NzListModule } from 'ng-zorro-antd/list';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { AccountModule } from '@abp/ng.account';
import { APP_INITIALIZER } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
// import { authInitializer } from './auth/auth-initializer';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { CurrentUserCacheService } from './service/current-user-cache.service';
import { CustomSpinComponent } from './custom-spin/custom-spin.component';

registerLocaleData(en);

function initializeUser(userCache: CurrentUserCacheService) {
  return () => userCache.load();
}

function initializeRefreshTokenLock(oAuthService: OAuthService) {
  return () => {
    const refreshToken = oAuthService.refreshToken.bind(oAuthService);
    let refreshInProgress: ReturnType<OAuthService['refreshToken']> | null = null;

    oAuthService.refreshToken = (() => {
      if (!refreshInProgress) {
        refreshInProgress = refreshToken().finally(() => {
          refreshInProgress = null;
        });
      }

      return refreshInProgress;
    }) as OAuthService['refreshToken'];
  };
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule.forRoot({
      environment,
      tenantKey: '__tenantid',
      registerLocaleFn: () => Promise.resolve('en')
    }),
    ThemeBasicModule.forRoot(),
    ThemeSharedModule.forRoot({
      // Our own AppErrorHandler (below) already shows a single, consistent toast for
      // every error status. Without this, ABP's own parallel error pipeline (unrelated
      // to Angular's ErrorHandler token) ALSO shows its own confirmation modal/overlay
      // for the same error, since ThemeSharedModule.forRoot() defaults to handling every
      // status unfiltered. 401 is intentionally excluded so ABP's built-in
      // AbpAuthenticationErrorHandler (auto-logout on an invalid session) keeps working.
      httpErrorConfig: {
        skipHandledErrorCodes: [0, 400, 403, 404, 500],
      },
    }),
    AccountConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    TenantManagementConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),
    NgxsModule.forRoot([AppState]),
    // NgxsModule.forFeature([AppState]),
    NgxsStoragePluginModule.forRoot({
      keys: ['AppState.pageLayoutContent']
    }),
    IconsProviderModule,
    NzLayoutModule,
    NzMenuModule,
    FormsModule,
    // HttpClientModule,
    NgxValidateCoreModule,
    CoreDirectivesModule,
    NzGridModule,
    NzSpaceModule,
    NzIconModule,
    NzDividerModule,
    NzInputModule,
    NzAutocompleteModule,
    NzTypographyModule,
    NzButtonModule,
    NzToolTipModule,
    NzAvatarModule,
    NzModalModule,
    NzDrawerModule,
    NzEmptyModule,
    NzBreadCrumbModule,
    NzPageHeaderModule,
    NzResultModule,
    NzDropDownModule,
    NzFormModule,
    NzUploadModule,
    NzMessageModule,
    NzSkeletonModule,
    NzAlertModule,
    NzCommentModule,
    NzRadioModule,
    NzSwitchModule,
    NzSelectModule,
    KeyboardShortcutsModule.forRoot(),
    NgxGoogleAnalyticsModule.forRoot(environment.googleAnalytics.measurementId),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
    NzListModule,
    AbpOAuthModule.forRoot(),
    AccountModule,
    CustomSpinComponent
  ],
  declarations: [
    AppComponent,
    SiteLayoutComponent,
    SiteHeaderComponent,
    SiteSiderComponent,
    PageLayoutComponent,
    BreadcrumbComponent,
    NotfoundComponent,
    SiteLoaderComponent,
    ValidationComponent,
    PortalLayoutComponent,
    PortalHeaderComponent,
    VendorLayoutComponent,
    ChangePasswordComponent,
    ManageProfileComponent,
    ManageProfileVendorComponent,
    SupportComponent,
    NotificationsComponent,
    AppearanceComponent,
    CallbackComponent,
    SignoutCallbackComponent
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler,
    },
    APP_ROUTE_PROVIDER,
    Title,
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: VALIDATION_BLUEPRINTS,
      useValue: {
        uniqueUsername: '::AlreadyExists[{{ name }}]',
      },
    },
    {
      provide: VALIDATION_ERROR_TEMPLATE,
      useValue: ValidationComponent,
    },
    GoogleSheetsDbService,
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantInterceptor,
      multi: true
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: '/tinymce/tinymce.min.js'
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeUser,
      deps: [CurrentUserCacheService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeRefreshTokenLock,
      deps: [OAuthService],
      multi: true,
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: authInitializer,
    //   deps: [OAuthService],
    //   multi: true,
    // },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
